import React, { useState, useCallback, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { storeLabels } from '../../lib/appLinks';
import { useScrollLock } from '../../hooks/useScrollLock';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import styles from './Header.module.css';

const Header: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  const toggleMobile = useCallback(() => {
    setMobileOpen((prev) => !prev);
  }, []);

  const closeMobile = useCallback(() => {
    setMobileOpen(false);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Pin page scroll when the mobile menu is open — never by hiding
  // the scrollbar (that shifts the page; see useScrollLock)
  useScrollLock(mobileOpen);

  // Trap focus inside the open menu and restore it to the toggle on
  // close (the hook remembers the previously-focused element). Attach
  // the ref to the panel; the toggle lives in <header>, so Esc closes
  // and the trap keeps Tab cycling the panel's own links.
  const menuRef = useFocusTrap<HTMLElement>(mobileOpen);

  // Escape closes the menu; on open, move focus into the panel.
  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeMobile();
    };
    document.addEventListener('keydown', onKey);
    // Focus the first link once the panel is interactive.
    const first = menuRef.current?.querySelector<HTMLElement>('a, button');
    const t = window.setTimeout(() => first?.focus(), 20);
    return () => {
      document.removeEventListener('keydown', onKey);
      window.clearTimeout(t);
    };
  }, [mobileOpen, closeMobile, menuRef]);

  const mobileStore = storeLabels();

  return (
    <>
      <header
        className={`${styles.header} ${scrolled ? styles.headerScrolled : ''}`}
        role="banner"
      >
        <div className={styles.container}>
          <Link to="/" className={styles.logo} aria-label="Muse — Home">
            <span className={styles.logoMark} aria-hidden="true">
              <img
                src="/muse-logo.png"
                alt=""
                width={28}
                height={28}
                className={styles.logoImage}
              />
            </span>
            <span className={styles.logoText}>Muse</span>
          </Link>

          <nav className={styles.nav} aria-label="Main navigation">
            <ul className={styles.navList}>
              <li>
                <a href="/#library" className={styles.navLink}>
                  Poses
                </a>
              </li>
              <li>
                <a href="/#workflow" className={styles.navLink}>
                  Workflow
                </a>
              </li>
              <li>
                <a href="/#galleries" className={styles.navLink}>
                  Galleries
                </a>
              </li>
              <li>
                <a href="/#faq" className={styles.navLink}>
                  FAQ
                </a>
              </li>
            </ul>
            <a href="/#get-the-app" className={styles.ctaButton}>
              Get the app
            </a>
          </nav>

          <button
            className={`${styles.mobileMenuButton} ${mobileOpen ? styles.mobileMenuButtonOpen : ''}`}
            onClick={toggleMobile}
            aria-expanded={mobileOpen}
            aria-controls="mobile-nav"
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
          >
            <span className={styles.hamburgerLine} />
            <span className={styles.hamburgerLine} />
            <span className={styles.hamburgerLine} />
          </button>
        </div>
      </header>

      {/* Mobile overlay — rendered outside header to escape its stacking context */}
      <div
        className={`${styles.mobileOverlay} ${mobileOpen ? styles.mobileOverlayOpen : ''}`}
        onClick={closeMobile}
        aria-hidden="true"
      />

      <nav
        id="mobile-nav"
        ref={menuRef}
        className={`${styles.mobileNav} ${mobileOpen ? styles.mobileNavOpen : ''}`}
        aria-label="Mobile navigation"
        // On mobile the panel stays in the DOM (display:flex, slid off-screen),
        // so without `inert` its links remain keyboard-tabbable and in the a11y
        // tree while the menu is visually closed. `inert` removes the whole
        // subtree from tab order + assistive tech when the menu is closed.
        {...(!mobileOpen ? { inert: '' as unknown as boolean } : {})}
      >
        <div className={styles.mobileNavInner}>
          <a
            href="/#library"
            className={styles.mobileNavLink}
            onClick={closeMobile}
          >
            Poses
          </a>
          <a
            href="/#workflow"
            className={styles.mobileNavLink}
            onClick={closeMobile}
          >
            Workflow
          </a>
          <a
            href="/#galleries"
            className={styles.mobileNavLink}
            onClick={closeMobile}
          >
            Galleries
          </a>
          <a href="/#faq" className={styles.mobileNavLink} onClick={closeMobile}>
            FAQ
          </a>
          <div className={styles.mobileNavDivider} />
          <div className={styles.mobileCtaGroup}>
            {mobileStore.interactive && mobileStore.apple.href ? (
              <>
                <a
                  href={mobileStore.apple.href}
                  className={styles.mobileCtaButton}
                  onClick={closeMobile}
                >
                  {mobileStore.apple.verb} {mobileStore.apple.name}
                </a>
                <a
                  href={mobileStore.play.href ?? '/#get-the-app'}
                  className={styles.mobileCtaButton}
                  onClick={closeMobile}
                >
                  {mobileStore.play.verb} {mobileStore.play.name}
                </a>
              </>
            ) : (
              <a
                href="/#get-the-app"
                className={styles.mobileCtaButton}
                onClick={closeMobile}
              >
                Get the app
              </a>
            )}
          </div>
        </div>
      </nav>
    </>
  );
};

export default Header;
